import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  DataSnapshot,
  Unsubscribe,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  onValue,
  ref,
  remove,
} from "firebase/database";
import { AppDispatch, RootState } from "src/store";
import { db } from "src/lib/firebase";

export type Platform = "android" | "ios" | string;

export interface PresenceUser {
  platform: Platform;
  ts: number;
  device_model?: string;
  package_name?: string;
  version?: string;
  build_number?: string;
}

export interface UserActivity {
  timestamp: number; // milliseconds since epoch (serializable)
  platform: Platform;
  action: "joined" | "left" | "updated";
  userId: string;
}

interface PresenceState {
  usersById: Record<string, PresenceUser>;
  isOnline: boolean;
  error: string | null;
  recentActivity: UserActivity[];
  lastTotalChangeMs: number;
}

const initialState: PresenceState = {
  usersById: {},
  isOnline: true,
  error: null,
  recentActivity: [],
  lastTotalChangeMs: 0,
};

const presenceSlice = createSlice({
  name: "presence",
  initialState,
  reducers: {
    setUsers(state, action: PayloadAction<Record<string, PresenceUser>>) {
      state.usersById = action.payload;
    },
    setIsOnline(state, action: PayloadAction<boolean>) {
      state.isOnline = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    addActivity(state, action: PayloadAction<UserActivity>) {
      state.recentActivity = [action.payload, ...state.recentActivity].slice(
        0,
        20,
      );
    },
    setLastTotalChangeMs(state, action: PayloadAction<number>) {
      state.lastTotalChangeMs = action.payload;
    },
    reset(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setUsers,
  setIsOnline,
  setError,
  addActivity,
  setLastTotalChangeMs,
  reset,
} = presenceSlice.actions;
export default presenceSlice.reducer;

// Module-scope unsubscribe holders
let unsubscribeFns: Unsubscribe[] = [];

function isPresenceUser(value: unknown): value is PresenceUser {
  if (!value || typeof value !== "object") return false;
  const v = value as any;
  return typeof v.platform === "string" && typeof v.ts === "number";
}

function parsePresenceUser(value: unknown): PresenceUser | null {
  if (!isPresenceUser(value)) return null;
  const v = value as any;
  return {
    platform: v.platform,
    ts: v.ts,
    device_model:
      typeof v.device_model === "string" ? v.device_model : undefined,
    package_name:
      typeof v.package_name === "string" ? v.package_name : undefined,
    version: typeof v.version === "string" ? v.version : undefined,
    build_number:
      typeof v.build_number === "string" ? v.build_number : undefined,
  };
}

export const startPresenceListeners = () => async (dispatch: AppDispatch) => {
  // Avoid double subscription
  if (unsubscribeFns.length > 0) return;

  // Note: We don't need anonymous auth since the Dashboard requires Google OAuth
  // and the authenticated Google user has database read permissions

  const connectedRef = ref(db, ".info/connected");
  const unsubConnected = onValue(connectedRef, (snap) => {
    dispatch(setIsOnline(!!snap.val()));
  });

  const usersRef = ref(db, "presence/users");

  const unsubValue = onValue(
    usersRef,
    (snapshot: DataSnapshot) => {
      const raw = (snapshot.val() || {}) as Record<string, unknown>;
      const next: Record<string, PresenceUser> = {};
      for (const [userId, value] of Object.entries(raw)) {
        const user = parsePresenceUser(value);
        if (user) {
          next[userId] = user;
        }
      }
      dispatch(setUsers(next));
      dispatch(setError(null));
    },
    (err: any) => {
      dispatch(setError(err?.message || "Failed to read presence data"));
    },
  );

  const unsubAdd = onChildAdded(usersRef, (snap: DataSnapshot) => {
    const userId = snap.key ?? "";
    const val = snap.val();
    if (!isPresenceUser(val)) return;
    const eventTimestamp = typeof val.ts === "number" ? val.ts : Date.now();
    dispatch(
      addActivity({
        userId,
        platform: val.platform,
        action: "joined",
        timestamp: eventTimestamp,
      }),
    );
    dispatch(setLastTotalChangeMs(eventTimestamp));
  });

  const unsubRemove = onChildRemoved(usersRef, (snap: DataSnapshot) => {
    const userId = snap.key ?? "";
    const val = snap.val();
    if (!isPresenceUser(val)) return;
    const eventTimestamp = Date.now();
    dispatch(
      addActivity({
        userId,
        platform: val.platform,
        action: "left",
        timestamp: eventTimestamp,
      }),
    );
    dispatch(setLastTotalChangeMs(eventTimestamp));
  });

  const unsubChange = onChildChanged(usersRef, (snap: DataSnapshot) => {
    const userId = snap.key ?? "";
    const val = snap.val();
    if (!isPresenceUser(val)) return;
    dispatch(
      addActivity({
        userId,
        platform: val.platform,
        action: "updated",
        timestamp: typeof val.ts === "number" ? val.ts : Date.now(),
      }),
    );
  });

  unsubscribeFns = [
    unsubConnected,
    unsubValue,
    unsubAdd,
    unsubRemove,
    unsubChange,
  ];
};

export const stopPresenceListeners = () => async (_dispatch: AppDispatch) => {
  if (unsubscribeFns.length > 0) {
    unsubscribeFns.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    unsubscribeFns = [];
  }
  // Do not reset Redux state here to avoid StrictMode flicker
};

/**
 * Deletes all presence entries whose `ts` is older than STALE_THRESHOLD_MS.
 * Returns the number of entries removed.
 */
export const deleteStaleEntries =
  () =>
  async (
    _dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<number> => {
    const usersById = getState().presence.usersById;
    const now = Date.now();
    const staleIds = Object.entries(usersById)
      .filter(([, user]) => now - user.ts >= STALE_THRESHOLD_MS)
      .map(([id]) => id);

    await Promise.all(
      staleIds.map((id) => remove(ref(db, `presence/users/${id}`))),
    );

    return staleIds.length;
  };

// Selectors
export const selectPresence = (state: RootState) => state.presence;
export const selectIsOnline = (state: RootState) => state.presence.isOnline;
export const selectError = (state: RootState) => state.presence.error;
export const selectUsersById = (state: RootState) => state.presence.usersById;
export const selectLastTotalChangeMs = (state: RootState) =>
  state.presence.lastTotalChangeMs;

// Flutter app heartbeats every 3 minutes; treat entries silent for >10 minutes as stale.
// This catches orphaned entries from processes killed without a clean disconnect.
export const STALE_THRESHOLD_MS = 10 * 60 * 1000;

export const selectActiveUsersById = createSelector(
  selectUsersById,
  (usersById) => {
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(usersById).filter(([, user]) => {
        return now - user.ts < STALE_THRESHOLD_MS;
      }),
    );
  },
);

export const selectStats = createSelector(
  selectActiveUsersById,
  selectLastTotalChangeMs,
  (usersById, lastTotalChangeMs) => {
    const values = Object.values(usersById);
    const android = values.filter(
      (u) => (u.platform || "").toLowerCase() === "android",
    ).length;
    const ios = values.filter(
      (u) => (u.platform || "").toLowerCase() === "ios",
    ).length;
    return {
      total: values.length,
      android,
      ios,
      lastUpdateMs: lastTotalChangeMs,
    } as const;
  },
);

// Analytics selectors for detailed breakdowns
export interface VersionBreakdown {
  version: string;
  count: number;
  platform: Platform;
  buildNumbers: string[];
  latestBuild: string;
}

export interface DeviceBreakdown {
  deviceModel: string;
  count: number;
  platforms: Set<Platform>;
}

export const selectVersionAnalytics = createSelector(
  selectActiveUsersById,
  (usersById) => {
    const users = Object.values(usersById);
    const versionMap = new Map<
      string,
      { count: number; platform: Platform; builds: Set<string> }
    >();

    users.forEach((user) => {
      if (user.version) {
        const key = `${user.platform}-${user.version}`;
        const existing = versionMap.get(key);
        if (existing) {
          existing.count++;
          if (user.build_number) {
            existing.builds.add(user.build_number);
          }
        } else {
          const builds = new Set<string>();
          if (user.build_number) {
            builds.add(user.build_number);
          }
          versionMap.set(key, {
            count: 1,
            platform: user.platform,
            builds,
          });
        }
      }
    });

    const breakdowns: VersionBreakdown[] = Array.from(versionMap.entries())
      .map(([key, data]) => {
        const version = key.substring(key.indexOf("-") + 1);
        const buildNumbers = Array.from(data.builds).sort((a, b) => {
          const numA = parseInt(a, 10);
          const numB = parseInt(b, 10);
          return isNaN(numA) || isNaN(numB) ? a.localeCompare(b) : numB - numA;
        });
        return {
          version,
          count: data.count,
          platform: data.platform,
          buildNumbers,
          latestBuild: buildNumbers[0] || "N/A",
        };
      })
      .sort((a, b) => {
        // Sort by platform first, then by count
        if (a.platform !== b.platform) {
          return a.platform.localeCompare(b.platform);
        }
        return b.count - a.count;
      });

    return breakdowns;
  },
);

export const selectDeviceAnalytics = createSelector(
  selectActiveUsersById,
  (usersById) => {
    const users = Object.values(usersById);
    const deviceMap = new Map<
      string,
      { count: number; platforms: Set<Platform> }
    >();

    users.forEach((user) => {
      if (user.device_model) {
        const existing = deviceMap.get(user.device_model);
        if (existing) {
          existing.count++;
          existing.platforms.add(user.platform);
        } else {
          deviceMap.set(user.device_model, {
            count: 1,
            platforms: new Set([user.platform]),
          });
        }
      }
    });

    const breakdowns: DeviceBreakdown[] = Array.from(deviceMap.entries())
      .map(([deviceModel, data]) => ({
        deviceModel,
        count: data.count,
        platforms: data.platforms,
      }))
      .sort((a, b) => b.count - a.count);

    return breakdowns;
  },
);

export const selectPackageAnalytics = createSelector(
  selectActiveUsersById,
  (usersById) => {
    const users = Object.values(usersById);
    const packageMap = new Map<string, number>();

    users.forEach((user) => {
      if (user.package_name) {
        packageMap.set(
          user.package_name,
          (packageMap.get(user.package_name) || 0) + 1,
        );
      }
    });

    return Array.from(packageMap.entries())
      .map(([packageName, count]) => ({ packageName, count }))
      .sort((a, b) => b.count - a.count);
  },
);
